import { type FC, useEffect, useState } from 'react';
import { Add, DeleteOutline, EditOutlined, SaveOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, IconButton, List, ListItem, ListItemText, Stack, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { updateOfferTechnicalAssesmentQuestions } from '@services/api/technical-assessment/updateQuestions/updateQuestions';
import { getOfferTechnicalAssesmentQuestions } from '@services/api/technical-assessment/getQuestions/getQuestions';
import EmptyAssessment from '@components/ui/offer-technical-assessment/assessment-list/EmptyAssessment';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './QuestionsList.translations.json';

type SupportedLanguage = keyof typeof translations;

export interface TechnicalAssessmentQuestion {
  id?: number | string;
  question: string;
}

interface QuestionsListProps {
  offerId: number;
}

const DARK_CARD_BG = '#121824';
const DARK_BORDER = '#1e2638';

const QuestionsList: FC<QuestionsListProps> = ({ offerId }) => {
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
    
    const [isSaving, setIsSaving] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [draftQuestion, setDraftQuestion] = useState('');
    const [questions, setQuestions] = useState<TechnicalAssessmentQuestion[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchTechnicalAssessmentList = async () => {
        setLoading(true)
        const response = await getOfferTechnicalAssesmentQuestions(offerId);
        const items = response.data || [];
        const results = (items || []).map((item: any, index: any) => {
          return {
            id: index,
            question: item?.question
          }
        })
        setQuestions(results || [])
        setLoading(false)
    };

    useEffect(() => {
        fetchTechnicalAssessmentList();
    }, []);

    const saveQuestions = async (nextQuestions: TechnicalAssessmentQuestion[]) => {
        setIsSaving(true);
        try {
            await updateOfferTechnicalAssesmentQuestions(offerId, nextQuestions.map(({ question }) => ({ question })));
            enqueueSnackbar(content.updated, { variant: 'success' });
            fetchTechnicalAssessmentList();
            return true;
        } catch (error) {
            enqueueSnackbar(content.updateError, { variant: 'error' });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleAdd = async () => {
        const question = draftQuestion.trim();
        if (!question) {
            enqueueSnackbar(content.enter, { variant: 'warning' });
            return;
        }
        if (await saveQuestions([...questions, { question }])) setDraftQuestion('');
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        setDraftQuestion(questions[index].question);
    };

    const handleUpdate = async () => {
        if (editingIndex === null) return;
        const question = draftQuestion.trim();
        if (!question) {
            enqueueSnackbar(content.enter, { variant: 'warning' });
            return;
        }
        const nextQuestions = questions.map((item, index) => index === editingIndex ? { ...item, question } : item);
        if (await saveQuestions(nextQuestions)) {
            setEditingIndex(null);
            setDraftQuestion('');
        }
    };

    const handleDelete = async (index: number) => {
        if (await saveQuestions(questions.filter((_, questionIndex) => questionIndex !== index)) && editingIndex === index) {
        setEditingIndex(null);
        setDraftQuestion('');
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 760, mx: 'auto', gap: 2, display: 'flex', flexDirection: 'column' }}>
            {questions?.length == 0 && !loading && (
                <EmptyAssessment offerId={offerId} onRefetch={fetchTechnicalAssessmentList} />
            )}
            <Alert severity="warning" sx={{ mt: 1, color: '#ffe0a3', backgroundColor: 'rgba(255, 167, 38, 0.12)', '& .MuiAlert-icon': { color: '#ffb74d' } }}>
               {content.notice}
            </Alert>
            {questions?.length > 0 && !loading && (
                
                <Card sx={{ maxWidth: 760, width: '100%', mx: 'auto', borderRadius: '16px', backgroundColor: DARK_CARD_BG, borderColor: DARK_BORDER, borderWidth: 1, borderStyle: 'solid', boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)', p: { xs: 1, sm: 2 } }}>
                    <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
                            <Box>
                                <Typography variant="h6" fontWeight={700} color="#ffffff">{content.title}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                            <TextField size="small" value={draftQuestion} onChange={(event) => setDraftQuestion(event.target.value)} placeholder={editingIndex === null ? content.new : content.edit} sx={{ minWidth: { xs: 0, sm: 260 }, '& .MuiInputBase-root': { color: '#ffffff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: DARK_BORDER } }} disabled={isSaving} />
                            <Button variant="contained" onClick={editingIndex === null ? handleAdd : handleUpdate} disabled={isSaving} startIcon={editingIndex === null ? <Add /> : <SaveOutlined />} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                                {editingIndex === null ? content.add : content.save}
                            </Button>
                            </Stack>
                        </Stack>

                        <List sx={{ p: 0 }}>
                            {loading && (
                            <ListItem>
                                <ListItemText primary={content.loading} primaryTypographyProps={{ color: '#ffffff', fontSize: '0.95rem', lineHeight: 1.5, fontWeight: 500 }} />
                            </ListItem>
                            )}
                            {questions.map((item, index) => (
                            <ListItem key={item.id ?? `${item.question}-${index}`} disableGutters sx={{ py: 1.25, borderBottom: index === questions.length - 1 ? 'none' : `1px solid ${DARK_BORDER}` }}>
                                <ListItemText primaryTypographyProps={{ color: '#ffffff', fontSize: '0.95rem', lineHeight: 1.5, fontWeight: 500 }} primary={`${index + 1}. ${item.question}`} />
                                <Stack direction="row" spacing={0.5} sx={{ ml: 2 }}>
                                <IconButton aria-label={content.editAria} onClick={() => handleEdit(index)} disabled={isSaving} sx={{ color: '#8c94a4' }}><EditOutlined fontSize="small" /></IconButton>
                                <IconButton aria-label={content.deleteAria} onClick={() => handleDelete(index)} disabled={isSaving} sx={{ color: '#f87171' }}><DeleteOutline fontSize="small" /></IconButton>
                                </Stack>
                            </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default QuestionsList;